import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, SupplierContract, SupplierContractInput, Vertragsart } from "@kundeninfo/api-client";
import { Arbeitsdialog, darfAbbrechen, useBeforeUnload, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KontierungFelder } from "../../components/KontierungFelder.js";
import type { KontierungWert } from "../../components/KontierungFelder.js";
import { KONTIERUNG_TEXT } from "../../components/kontierungTexts.js";
import { errorProp } from "../../components/fieldErrors.js";
import { KiKnopf } from "../../components/meHuellen.js";
import { laufzeitPaar } from "../../components/laufzeitPaar.js";
import { Vorgangsabschnitt } from "../../components/Vorgangsabschnitt.js";
import { useSectionFolds } from "../../components/vorgangsabschnitt/sectionFolds.js";
import { formatDatum } from "../../format.js";
import { NOTE_MAX, PAYMENT_METHODS, PLAN_FROM_DEFAULT, PLAN_YEAR_MAX, PLAN_YEAR_MIN, REFERENCE_MAX, TERM_VALUE_MAX, TITLE_MAX,
  defaultPlanFrom, today, wholeNumber } from "./shared.js";
import { TERM_FIELDS, TermFields, termBody, termDraftOf, termFieldErrors, useActiveRates } from "./KonditionPanel.js";
import type { TermDraft } from "./KonditionPanel.js";
import { SUPPLIER_TEXT_WEB as T } from "./texts.js";
import "./Suppliers.css";

/**
 * DAS VERTRAGSPANEL — Liefervertrag anlegen oder bearbeiten (Spec „Lieferverträge“, „Akte eines Lieferanten“; Plan
 * Aufgabe 13): Modalpanel rechts im Muster des Auftragsdialogs (`Arbeitsdialog panel`, unter 1024 px vollflächig) in
 * Abschnitten — Vertrag, Laufzeit und Kündigung, Kontierung, beim Anlegen die erste Kondition.
 *
 *  - **Vertragsart** belegt beim Anlegen Abrechnungsweise, Rhythmus der ersten Kondition, Zahlweise und das Vorgabekonto
 *    vor — das Konto des Kontenrahmens, den das Unternehmen führt (das Konto der Art, das in der Kontenliste des
 *    Unternehmens steht). Bis jemand die Kontierung selbst ändert, folgt sie der Art; ist die Liste noch nicht da, geht
 *    das Konto gar nicht mit (der Server nimmt dann die Vorgabe der Art).
 *  - **Laufzeit** wie am Auftrag (P26): Mindestlaufzeit, Verlängerung und Kündigungsfrist je als Zahl mit Einheit
 *    „Monate“/„Jahre“; leer heißt Wert und Einheit `null`, eine Frist ohne Zahl auch ohne Bezug. Entscheid 544 („Hetzner,
 *    täglich kündbar“): Mindestlaufzeit und Verlängerung ab 0 (0 = keine, der Server speichert sie leer), die
 *    Kündigungsfrist auch in Tagen ab 0 und mit dem Bezug „jederzeit“ (`IMMEDIATE`).
 *  - **Vorbelegung** (Entscheid 543): Der Vertragsbeginn ist beim Anlegen HEUTE als Wert (ein leeres Datumsfeld zeigt in
 *    Safari das heutige Datum nur grau als Platzhalter); der Vorgabezustand ist ohne weitere Eingabe speicherbar.
 *  - **Grenzen des Schemas** setzt die Maske selbst durch (Längen, ganze Zahlen ≤ 120, Betragsform, Pflichtdaten).
 *  - **Fälligkeitenplan ab (Monat)** (Dirk 511): Monat und Jahr als Paar über die ganze Zeile, der Hinweis darunter
 *    (A-W4); leer heißt die Vorgabe (der Hinweis nennt den wirksamen Tag, nie einen Beginn vor 2026),
 *    gewählt geht der Erste des Monats als `planFrom` mit. Die Monatswahl belegt ein leeres Jahr mit dem der Vorgabe vor
 *    (Beginn bzw. 2026), „Vorgabe“ leert es; solange der Monat „Vorgabe“ ist, ist das Jahr gesperrt und zählt nicht
 *    (Entscheid 543 — vorher meldete ein Jahr ohne Monat einen Befund und hielt den Vorgabezustand fest).
 *    Die Fachregeln (nicht vor dem Monat des Beginns, nicht nach dem Ende) meldet der Server am Monat.
 *  - Befunde stehen an dem Feld, das der Server nennt: „überschneidet sich mit festen Posten“ am Beginn, die Lücke an
 *    festen Posten nach einer Verlängerung (`planGapAtFixed`) meldet er selbst am Vertragsende (Final-Fixrunde).
 *
 * Die Kontierungslisten kommen vom Reiter (`lists`, einmal je Reiter); die Steuersätze lädt das Panel nur beim Anlegen
 * (erste Kondition). Der Speichern-Knopf ist die Bestätigung (Dirk 216c); Abbrechen ist der eine Ausgang daneben (Rückfrage bei
 * Ungespeichertem, `useBeforeUnload`); eine 409 zeigt den fremden Stand mit „Neu laden“.
 */
type Unit = NonNullable<SupplierContract["minimumTermUnit"]>;
type NoticeUnit = NonNullable<SupplierContract["noticePeriodUnit"]>;
type BillingMode = SupplierContract["billingMode"];
type PaymentMethod = SupplierContract["paymentMethod"];
type NoticeReference = NonNullable<SupplierContract["noticeReference"]>;

const BILLING_MODES: readonly BillingMode[] = ["INVOICE_PER_PERIOD", "FIXED_PAYMENT", "INSTALMENT_SETTLEMENT"];
const NOTICE_REFERENCES: readonly NoticeReference[] = ["TERM_END", "CALENDAR_YEAR_END", "IMMEDIATE"];
/** Die Einheiten der Kündigungsfrist (Entscheid 544); Mindestlaufzeit und Verlängerung bleiben Monate/Jahre. */
const NOTICE_UNITS: readonly NoticeUnit[] = ["DAYS", "MONTHS", "YEARS"];
const MONTH_NUMBERS = Array.from({ length: 12 }, (_, i) => i + 1);
const FIRST_TERM = "firstTerm.";

type Pair<U extends string = Unit> = { value: string; unit: U };
type Draft = {
  typeId: string; title: string; reference: string; validFrom: string; validTo: string; minimum: Pair; renewal: Pair;
  notice: Pair<NoticeUnit>; noticeReference: NoticeReference; billingMode: BillingMode; paymentMethod: PaymentMethod;
  settlementMonth: string; planMonth: string; planYear: string; assignment: KontierungWert; assignmentTouched: boolean;
  note: string; term: TermDraft;
};

const pairOf = <U extends NoticeUnit>(value: number | null, unit: U | null): Pair<U | "MONTHS"> =>
  ({ value: value === null ? "" : String(value), unit: unit ?? "MONTHS" });
const EMPTY_PAIR: Pair<"MONTHS"> = { value: "", unit: "MONTHS" };

function draftOf(c: SupplierContract | null): Draft {
  if (c === null) {
    return { typeId: "", title: "", reference: "", validFrom: today(), validTo: "", minimum: EMPTY_PAIR,
      renewal: EMPTY_PAIR, notice: EMPTY_PAIR, noticeReference: "TERM_END",
      billingMode: "INVOICE_PER_PERIOD", paymentMethod: "TRANSFER", settlementMonth: "", planMonth: "", planYear: "",
      assignment: { bookingAccountId: null, costCentreId: null }, assignmentTouched: false, note: "", term: termDraftOf(null) };
  }
  return {
    typeId: c.contractTypeId, title: c.title, reference: c.supplierReference ?? "", validFrom: c.validFrom,
    validTo: c.validTo ?? "", minimum: pairOf(c.minimumTermValue, c.minimumTermUnit),
    renewal: pairOf(c.renewalPeriodValue, c.renewalPeriodUnit), notice: pairOf(c.noticePeriodValue, c.noticePeriodUnit),
    noticeReference: c.noticeReference ?? "TERM_END", billingMode: c.billingMode, paymentMethod: c.paymentMethod,
    settlementMonth: c.settlementMonth === null ? "" : String(c.settlementMonth),
    planMonth: c.planFrom === null ? "" : String(Number(c.planFrom.slice(5, 7))), planYear: c.planFrom?.slice(0, 4) ?? "",
    assignment: { bookingAccountId: c.bookingAccountId, costCentreId: c.costCentreId }, assignmentTouched: true,
    note: c.note ?? "", term: termDraftOf(null),
  };
}

/** Wert und Einheit für den Server: leer heißt beides `null` (P26). Nur nach einer Prüfung ohne Befund. Alle drei Paare
 *  beginnen bei 0 (Entscheid 544): eine Laufzeitgröße 0 speichert der Server als „keine“, die Frist 0 bleibt 0. */
const pairFor = <U extends string>(p: Pair<U>): { value: number | null; unit: U | null } => {
  const value = wholeNumber(p.value, 0, TERM_VALUE_MAX) ?? null;
  return { value, unit: value === null ? null : p.unit };
};

const CONTRACT_FIELDS = new Set(["contractTypeId", "title", "supplierReference", "validFrom", "validTo",
  "minimumTermValue", "minimumTermUnit", "renewalPeriodValue", "renewalPeriodUnit", "noticePeriodValue",
  "noticePeriodUnit", "noticeReference", "billingMode", "paymentMethod", "settlementMonth", "planFrom", "bookingAccountId",
  "costCentreId", "responsibleUserId", "note", "companyId"]);
/** `planFromYear` prüft nur die Maske (der Server kennt das Jahr nicht als eigenes Feld). */
const RUNTIME_FIELDS = new Set(["validFrom", "validTo", "minimumTermValue", "minimumTermUnit", "renewalPeriodValue",
  "renewalPeriodUnit", "noticePeriodValue", "noticePeriodUnit", "noticeReference", "planFrom", "planFromYear"]);
const ASSIGNMENT_FIELDS = new Set(["bookingAccountId", "costCentreId"]);
const isTermField = (f: string): boolean => f.startsWith(FIRST_TERM)
  && (TERM_FIELDS as readonly string[]).includes(f.slice(FIRST_TERM.length));
const knownField = (f: string): boolean => CONTRACT_FIELDS.has(f) || isTermField(f);

/** Die Prüfung der Maske vor dem Senden (Pflicht, Grenzen des Schemas) — Schlüssel wie die Felder des Servers. */
function check(d: Draft, withTerm: boolean, rates: Parameters<typeof termFieldErrors>[1]): Record<string, string> {
  const e: Record<string, string> = {};
  if (d.typeId === "") e.contractTypeId = T.choiceRequired;
  if (d.validFrom === "") e.validFrom = T.dateRequired;
  const pairs: [Pair<string>, string][] = [[d.minimum, "minimumTermValue"], [d.renewal, "renewalPeriodValue"],
    [d.notice, "noticePeriodValue"]];
  for (const [p, field] of pairs) {
    if (wholeNumber(p.value, 0, TERM_VALUE_MAX) === undefined) e[field] = T.wholeNumber(0, TERM_VALUE_MAX);
  }
  // Das Jahr zählt nur mit einem Monat (543): bei „Vorgabe“ ist es gesperrt und wird nicht geprüft.
  if (d.planMonth !== "" && !wholeNumber(d.planYear, PLAN_YEAR_MIN, PLAN_YEAR_MAX)) {
    e.planFromYear = T.wholeNumber(PLAN_YEAR_MIN, PLAN_YEAR_MAX);
  }
  return withTerm ? { ...e, ...termFieldErrors(d.term, rates, FIRST_TERM, false) } : e;
}

export type ContractPanelClient = Pick<ApiClient, "getSupplierContractTypes" | "createSupplierContract"
  | "updateSupplierContract" | "getSupplierContract" | "listActiveTaxRates" | "getBookingAccounts" | "getCostCentres">;

export function VertragPanel(p: {
  customerId: string; companyId: string; client: ContractPanelClient; vorhandener?: SupplierContract;
  /** Die Kontierungslisten des Reiters (`sharedAssignmentLists`, A-K3). */
  lists: Pick<ApiClient, "getBookingAccounts" | "getCostCentres">;
  onGespeichert: (c: SupplierContract) => void; onSchliessen: (reload: boolean) => void;
  /** Ein Schritt im Browserverlauf (Entscheid 696, PF9): die Zurück-Taste schließt das Panel wie Abbrechen. */
  verlauf?: boolean;
}): ReactElement {
  const { client, customerId } = p;
  const [saved, setSaved] = useState<SupplierContract | null>(p.vorhandener ?? null);
  const [start, setStart] = useState<Draft>(() => draftOf(p.vorhandener ?? null));
  const [draft, setDraft] = useState<Draft>(start);
  const [tried, setTried] = useState(false);
  const [types, setTypes] = useState<Vertragsart[]>([]);
  const [typesFailed, setTypesFailed] = useState(false);
  const [companyAccounts, setCompanyAccounts] = useState<ReadonlySet<string> | null>(null);
  const lists = p.lists;
  // Nur die erste Kondition (beim Anlegen) braucht die Sätze — beim Bearbeiten kein Abruf und kein Hinweis ohne Anlass.
  const rateState = useActiveRates(client, p.vorhandener === undefined);
  const rates = rateState.rates;
  const sections = useSectionFolds({ contract: true, runtime: true, assignment: true, term: true });
  const creating = saved === null;
  const companyId = saved?.companyId ?? p.companyId;

  useEffect(() => {
    let alive = true;
    client.getSupplierContractTypes().then((r) => { if (alive) setTypes(r.items); }, () => { if (alive) setTypesFailed(true); });
    // Dieselbe Abfrage wie die Kontofelder (`KontierungFelder`) — der gemeinsame Speicher beantwortet sie einmal.
    lists.getBookingAccounts({ companyId, kind: "EXPENSE" })
      .then((r) => { if (alive) setCompanyAccounts(new Set(r.items.map((k) => k.id))); }, () => undefined);
    return () => { alive = false; };
  }, [client, lists, companyId]);

  /** Das Vorgabekonto der Art im Rahmen des Unternehmens; `undefined`, solange die Kontenliste fehlt. */
  const defaultAccount = (typeId: string): string | null | undefined => (companyAccounts === null ? undefined
    : types.find((t) => t.id === typeId)?.accounts.find((a) => companyAccounts.has(a.bookingAccountId))?.bookingAccountId
      ?? null);
  const followsType = creating && !draft.assignmentTouched;
  const assignment: KontierungWert = followsType
    ? { ...draft.assignment, bookingAccountId: defaultAccount(draft.typeId) ?? null } : draft.assignment;

  const set = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));
  function chooseType(id: string): void {
    const t = types.find((x) => x.id === id);
    if (!creating || t === undefined) { set({ typeId: id }); return; }
    setDraft((d) => ({ ...d, typeId: id, billingMode: t.defaultBillingMode, paymentMethod: t.defaultPaymentMethod,
      assignmentTouched: false, term: { ...d.term, cycle: t.defaultCycle } }));
  }

  /** Monat des Planbeginns; ein leeres Jahr bekommt das der Vorgabe (Beginn bzw. 2026), „Vorgabe“ leert beides. */
  function choosePlanMonth(month: string): void {
    setDraft((d) => ({ ...d, planMonth: month, planYear: month === "" ? ""
      : d.planYear.trim() === "" ? defaultPlanFrom(d.validFrom).slice(0, 4) : d.planYear }));
  }

  function contractInput(): SupplierContractInput {
    const d = draft;
    const minimum = pairFor(d.minimum);
    const renewal = pairFor(d.renewal);
    const notice = pairFor(d.notice);
    const account = followsType ? defaultAccount(d.typeId) : assignment.bookingAccountId;
    return {
      contractTypeId: d.typeId, title: d.title.trim(), supplierReference: d.reference.trim() === "" ? null : d.reference.trim(),
      validFrom: d.validFrom, validTo: d.validTo === "" ? null : d.validTo,
      minimumTermValue: minimum.value, minimumTermUnit: minimum.unit,
      renewalPeriodValue: renewal.value, renewalPeriodUnit: renewal.unit,
      noticePeriodValue: notice.value, noticePeriodUnit: notice.unit,
      noticeReference: notice.value === null ? null : d.noticeReference,
      billingMode: d.billingMode, paymentMethod: d.paymentMethod,
      settlementMonth: d.billingMode === "INSTALMENT_SETTLEMENT" && d.settlementMonth !== "" ? Number(d.settlementMonth) : null,
      planFrom: d.planMonth === "" ? null : `${d.planYear.trim()}-${d.planMonth.padStart(2, "0")}-01`,
      // Ohne Kontenliste geht das Konto beim Anlegen gar nicht mit: Dann gilt die Vorgabe der Art (Aufgabe 12).
      ...(account === undefined ? {} : { bookingAccountId: account }),
      costCentreId: assignment.costCentreId, responsibleUserId: saved?.responsibleUserId ?? null,
      note: d.note.trim() === "" ? null : d.note.trim(),
    };
  }

  const form = useFormularZustand<SupplierContract>({
    speichern: () => (saved === null
      ? client.createSupplierContract(customerId, { companyId, contract: contractInput(), firstTerm: termBody(draft.term, rates) })
      : client.updateSupplierContract(customerId, saved.id, { version: saved.version, contract: contractInput() })),
    neuLaden: async () => {
      if (saved === null) return;
      const c = await client.getSupplierContract(customerId, saved.id);
      const d = draftOf(c);
      setSaved(c);
      setStart(d);
      setDraft(d);
      setTried(false);
    },
    bekannteFelder: knownField,
  });

  // Nach einem fremden Stand (409) lädt das Schließen die Liste neu (K3) — sie trägt sonst die alte Version.
  const conflicted = useRef(false);
  useEffect(() => { if (form.zustand === "fremder-stand") conflicted.current = true; }, [form.zustand]);

  const localErrors = check(draft, creating, rates);
  const errors: Record<string, string> = { ...(tried ? localErrors : {}), ...form.feldFehler };
  const dirty = JSON.stringify(draft) !== JSON.stringify(start);
  useBeforeUnload(dirty);

  async function save(): Promise<void> {
    setTried(true);
    if (Object.keys(localErrors).length > 0) return;
    const r = await form.aktion();
    if (r !== undefined) p.onGespeichert(r);
  }
  async function leave(): Promise<void> {
    if (form.laeuft) return;
    if (await darfAbbrechen(dirty)) p.onSchliessen(conflicted.current);
  }

  const count = (pick: (f: string) => boolean): number => Object.keys(errors).filter(pick).length;
  /** Ein Laufzeitpaar wie an Angebot und Auftrag (`laufzeitPaar`, Teil C K5); `units` nur an der Kündigungsfrist (544). */
  const pairField = <U extends NoticeUnit>(label: string, field: string, pair: Pair<U>, onPair: (p: Pair<U>) => void,
    units?: readonly U[]): ReactElement => laufzeitPaar<U>({
    block: "sup", pairClass: "sup__pair", id: `sup-${field}`, label, einheitLabel: T.unitOf(label), wert: pair.value,
    einheit: pair.unit, onWert: (value) => onPair({ ...pair, value }), onEinheit: (unit) => onPair({ ...pair, unit }),
    fehler: errors[field], ...(units === undefined ? {} : { einheiten: units }) });
  // Eine gespeicherte, inzwischen deaktivierte Vertragsart bleibt als Option stehen (sie fehlt in der aktiven Liste).
  const storedType = saved !== null && !types.some((t) => t.id === saved.contractTypeId) ? saved : null;

  const contractFields = (
    <div className="sup__grid">
      <Auswahl beschriftung={T.contractType} wert={draft.typeId} {...errorProp(errors.contractTypeId)}
        onGeaendert={(e) => chooseType(e.wert)}>
        {draft.typeId === "" && <option value="">{T.choose}</option>}
        {storedType !== null && <option value={storedType.contractTypeId}>{T.inactiveType(storedType.contractTypeName)}</option>}
        {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </Auswahl>
      <Eingabe beschriftung={T.contractTitle} wert={draft.title} pflicht maxLength={TITLE_MAX} {...errorProp(errors.title)}
        onGeaendert={(e) => set({ title: e.wert })} />
      <Eingabe beschriftung={T.reference} wert={draft.reference} maxLength={REFERENCE_MAX}
        {...errorProp(errors.supplierReference)} onGeaendert={(e) => set({ reference: e.wert })} />
      <Auswahl beschriftung={T.billingModeLabel} wert={draft.billingMode} {...errorProp(errors.billingMode)}
        onGeaendert={(e) => set({ billingMode: e.wert as BillingMode })}>
        {BILLING_MODES.map((m) => <option key={m} value={m}>{T.billingMode[m]}</option>)}
      </Auswahl>
      <Auswahl beschriftung={T.paymentMethodLabel} wert={draft.paymentMethod} {...errorProp(errors.paymentMethod)}
        onGeaendert={(e) => set({ paymentMethod: e.wert as PaymentMethod })}>
        {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{T.paymentMethod[m]}</option>)}
      </Auswahl>
      {draft.billingMode === "INSTALMENT_SETTLEMENT" && (
        <Auswahl beschriftung={T.settlementMonth} wert={draft.settlementMonth} {...errorProp(errors.settlementMonth)}
          onGeaendert={(e) => set({ settlementMonth: e.wert })}>
          <option value="">{T.choose}</option>
          {MONTH_NUMBERS.map((m) => <option key={m} value={String(m)}>{T.month(m)}</option>)}
        </Auswahl>
      )}
      <Eingabe className="sup__wide" beschriftung={T.note} mehrzeilig wert={draft.note} maxLength={NOTE_MAX}
        {...errorProp(errors.note)} onGeaendert={(e) => set({ note: e.wert })} />
    </div>
  );

  const runtimeFields = (
    <div className="sup__grid">
      <Eingabe art="datum" beschriftung={T.validFrom} wert={draft.validFrom} pflicht {...errorProp(errors.validFrom)}
        onGeaendert={(e) => set({ validFrom: e.wert })} />
      <Eingabe art="datum" beschriftung={T.validTo} wert={draft.validTo} {...errorProp(errors.validTo)}
        onGeaendert={(e) => set({ validTo: e.wert })} />
      <div className="sup__wide sup__plan">
        <div className="sup__pair">
          <Auswahl beschriftung={T.planFrom} wert={draft.planMonth} {...errorProp(errors.planFrom)}
            onGeaendert={(e) => choosePlanMonth(e.wert)}>
            <option value="">{T.planFromDefault}</option>
            {MONTH_NUMBERS.map((m) => <option key={m} value={String(m)}>{T.month(m)}</option>)}
          </Auswahl>
          <Eingabe art="zahl" beschriftung={T.planFromYear} wert={draft.planMonth === "" ? "" : draft.planYear}
            gesperrt={draft.planMonth === ""} {...errorProp(errors.planFromYear)}
            onGeaendert={(e) => set({ planYear: e.wert })} />
        </div>
        <p className="sup__hint">
          {T.planFromHint(formatDatum(defaultPlanFrom(draft.validFrom)), formatDatum(PLAN_FROM_DEFAULT))}</p>
      </div>
      {pairField(T.minimumTerm, "minimumTermValue", draft.minimum, (minimum) => set({ minimum }))}
      {pairField(T.renewal, "renewalPeriodValue", draft.renewal, (renewal) => set({ renewal }))}
      {pairField(T.noticePeriod, "noticePeriodValue", draft.notice, (notice) => set({ notice }), NOTICE_UNITS)}
      <Auswahl beschriftung={T.noticeReferenceLabel} wert={draft.noticeReference} {...errorProp(errors.noticeReference)}
        onGeaendert={(e) => set({ noticeReference: e.wert as NoticeReference })}>
        {NOTICE_REFERENCES.map((r) => <option key={r} value={r}>{T.noticeReference[r]}</option>)}
      </Auswahl>
    </div>
  );

  const messages = [
    ...(form.meldung === null ? [] : [<Hinweis key="m" art="fehler">{form.meldung}</Hinweis>]),
    ...(typesFailed ? [<Hinweis key="t" art="warnung">{T.typesLoadFailed}</Hinweis>] : []),
    ...(rateState.failed ? [<Hinweis key="r" art="warnung">{T.ratesLoadFailed}</Hinweis>] : []),
  ];

  return (
    <Arbeitsdialog titel={saved === null ? T.newContract : T.editTitle(saved.title)} panel="supplier-contract"
      {...(p.verlauf === true ? { verlauf: true } : {})}
      onAbbrechen={leave} abbrechenGesperrt={form.laeuft}
      {...(messages.length === 0 ? {} : { meldungen: <>{messages}</> })}
      fuss={(
        <>
          <KiKnopf art="primaer" deaktiviert={form.zustand !== "bereit"} onClick={() => { void save(); }}>{T.save}</KiKnopf>
          <KiKnopf deaktiviert={form.laeuft} onClick={() => { void leave(); }}>{T.cancel}</KiKnopf>
        </>
      )}>
      <Formular beschriftung="oben" {...form.formularProps}>
        <div className="sup__mask">
          <Vorgangsabschnitt art="vertrag" titel={T.sectionContract} faltung={sections.contract} inhaltId="sup-vertrag"
            fehler={count((f) => CONTRACT_FIELDS.has(f) && !RUNTIME_FIELDS.has(f) && !ASSIGNMENT_FIELDS.has(f))}>
            {contractFields}
          </Vorgangsabschnitt>
          <Vorgangsabschnitt art="abrechnung" titel={T.sectionRuntime} faltung={sections.runtime} inhaltId="sup-laufzeit"
            fehler={count((f) => RUNTIME_FIELDS.has(f))}>{runtimeFields}</Vorgangsabschnitt>
          <Vorgangsabschnitt art={null} titel={T.sectionAssignment} faltung={sections.assignment} inhaltId="sup-kontierung"
            fehler={count((f) => ASSIGNMENT_FIELDS.has(f))}>
            <KontierungFelder point="SUPPLIER_CONTRACT" companyId={companyId} client={lists} wert={assignment}
              hint={KONTIERUNG_TEXT.contractDefault}
              fehler={{ ...(errors.bookingAccountId === undefined ? {} : { bookingAccountId: errors.bookingAccountId }),
                ...(errors.costCentreId === undefined ? {} : { costCentreId: errors.costCentreId }) }}
              onWert={(w) => set({ assignment: w, assignmentTouched: true })} />
          </Vorgangsabschnitt>
          {creating && (
            <Vorgangsabschnitt art="position" titel={T.sectionFirstTerm} faltung={sections.term} inhaltId="sup-kondition"
              fehler={count(isTermField)}>
              <TermFields draft={draft.term} onDraft={(patch) => setDraft((d) => ({ ...d, term: { ...d.term, ...patch } }))}
                rates={rateState} errors={errors} prefix={FIRST_TERM} withValidFrom={false} />
            </Vorgangsabschnitt>
          )}
        </div>
      </Formular>
    </Arbeitsdialog>
  );
}
